import {
    Button,
    Card,
    IconButton,
    InputAdornment,
    Typography,
    Tooltip,
    TextField,
    Drawer
} from '@material-ui/core';
import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import HistoryIcon from '@material-ui/icons/History';
import CloseIcon from '@material-ui/icons/Close';
import SearchIcon from '@material-ui/icons/Search';
import ListAltIcon from '@material-ui/icons/ListAlt';
import { toHumanReadableFriendlyDateTime } from 'helpers/date';

/* ── Nombre de modifications affichées dans la preview ── */
const HISTORY_PREVIEW_COUNT = 3;

/* ── Une entrée d'historique (factorisée pour preview + drawer) ── */
const HistoryEntry = ({ entry, classes, rootClassName }) => (
    <div className={rootClassName}>
        <div className={classes.historyEntryHead}>
            <div className={classes.historyEntryMeta}>
                <Typography className={classes.historyEntryDate}>
                    {toHumanReadableFriendlyDateTime(entry.at.date)}
                </Typography>
                {entry.author && (
                    <Typography className={classes.historyEntryAuthor}>{entry.author}</Typography>
                )}
            </div>

            {entry.previous_bill_number && (
                <Tooltip title="Consulter la facture annulée">
                    <RouterLink
                        target="_blank"
                        to={`/bill/edit/${entry.previous_bill_number}`}
                        className={classes.historyEntryLink}>
                        {entry.previous_bill_number}
                        <OpenInNewIcon />
                    </RouterLink>
                </Tooltip>
            )}
        </div>

        <div className={classes.historyChanges}>
            {entry.changes.map((change, idx) => (
                <div key={idx} className={classes.changeRow}>
                    <Typography className={classes.changeField}>{change.field}</Typography>
                    <div className={classes.changeValues}>
                        {change.old && (
                            <>
                                <Typography component="span" className={classes.changeOld}>
                                    {change.old}
                                </Typography>
                                <span className={classes.changeArrow}>→</span>
                            </>
                        )}
                        <Typography component="span" className={classes.changeNew}>
                            {change.new}
                        </Typography>
                    </div>
                </div>
            ))}
        </div>
    </div>
);

const HistoryCard = ({ history, billNumber, classes }) => {
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [search, setSearch] = useState('');

    const total = history ? history.length : 0;
    const preview = history ? history.slice(0, HISTORY_PREVIEW_COUNT) : [];
    const remaining = total - preview.length;

    /* Filtrage de l'historique complet dans le drawer */
    const filteredHistory = (() => {
        if (!history) return [];
        const q = search.trim().toLowerCase();
        if (!q) return history;
        return history.filter(entry => {
            if (entry.author && entry.author.toLowerCase().includes(q)) return true;
            if (
                entry.previous_bill_number &&
                entry.previous_bill_number.toLowerCase().includes(q)
            ) {
                return true;
            }
            return entry.changes?.some(
                change =>
                    change.field?.toLowerCase().includes(q) ||
                    String(change.old ?? '')
                        .toLowerCase()
                        .includes(q) ||
                    String(change.new ?? '')
                        .toLowerCase()
                        .includes(q)
            );
        });
    })();

    return (
        <>
            <Card className={classes.historyCard}>
                <div className={classes.historyHeader}>
                    <HistoryIcon className={classes.historyHeaderIcon} />
                    <Typography className={classes.historyTitle}>Historique</Typography>
                    {total > 0 && (
                        <Typography variant="body2" className={classes.historyCount}>
                            {total}
                        </Typography>
                    )}
                </div>

                {total === 0 ? (
                    <Typography className={classes.historyEmpty}>
                        Aucune modification enregistrée.
                    </Typography>
                ) : (
                    <>
                        {preview.map(entry => (
                            <HistoryEntry
                                key={entry.id}
                                entry={entry}
                                classes={classes}
                                rootClassName={classes.historyEntry}
                            />
                        ))}

                        {remaining > 0 && (
                            <div className={classes.seeAllRow}>
                                <Button
                                    className={classes.seeAllBtn}
                                    startIcon={<ListAltIcon />}
                                    onClick={() => setDrawerOpen(true)}>
                                    Voir les {remaining} modification
                                    {remaining > 1 ? 's' : ''} précédente
                                    {remaining > 1 ? 's' : ''}
                                </Button>
                            </div>
                        )}
                    </>
                )}
            </Card>

            <Drawer
                anchor="right"
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                classes={{ paper: classes.drawerPaper }}>
                <div className={classes.drawerHeader}>
                    <HistoryIcon className={classes.drawerHeaderIcon} />
                    <div className={classes.drawerTitleBlock}>
                        <Typography className={classes.drawerTitle}>
                            Historique complet
                            {billNumber && ` · Facture ${billNumber}`}
                        </Typography>
                        <Typography className={classes.drawerSub}>
                            {total} modification{total > 1 ? 's' : ''} enregistrée
                            {total > 1 ? 's' : ''}
                        </Typography>
                    </div>
                    <IconButton
                        onClick={() => setDrawerOpen(false)}
                        size="small"
                        aria-label="Fermer">
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </div>

                <div className={classes.drawerSearchRow}>
                    <TextField
                        fullWidth
                        size="small"
                        variant="outlined"
                        placeholder="Rechercher par champ, auteur ou facture…"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            )
                        }}
                    />
                </div>

                <div className={classes.drawerBody}>
                    {filteredHistory.length === 0 ? (
                        <div className={classes.drawerEmpty}>
                            Aucune modification ne correspond à votre recherche.
                        </div>
                    ) : (
                        filteredHistory.map(entry => (
                            <HistoryEntry
                                key={entry.id}
                                entry={entry}
                                classes={classes}
                                rootClassName={classes.drawerEntry}
                            />
                        ))
                    )}
                </div>
            </Drawer>
        </>
    );
};

export default HistoryCard;
